import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Pencil, Copy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CopyRulesDialog } from "./CopyRulesDialog";

interface FieldRulesListProps {
  fieldId: string;
}

export const FieldRulesList = ({ fieldId }: FieldRulesListProps) => {
  const [editingRule, setEditingRule] = useState<Tables<"field_rules"> | null>(null);
  const [newRuleText, setNewRuleText] = useState("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch field name for the current field
  const { data: fieldInfo } = useQuery({
    queryKey: ["field-info", fieldId],
    queryFn: async () => {
      const { data, error } = await supabase.from("fields").select("name").eq("id", fieldId).single();

      if (error) {
        console.error("Failed to load field info:", error);
        return { name: "This field" };
      }
      return data;
    },
  });

  const { data: rules, isLoading } = useQuery({
    queryKey: ["field-rules", fieldId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("field_rules")
        .select("*")
        .eq("field_id", fieldId)
        .order("created_at");

      if (error) {
        toast.error("Failed to load field rules");
        throw error;
      }
      return data || [];
    },
  });

  const { data: fieldUsage } = useQuery({
    queryKey: ["field-usage", fieldId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("template_fields")
        .select(
          `
          id,
          template:templates (
            id,
            name
          ),
          rule_sets:template_field_rule_sets (
            id,
            name,
            template_field_rules (
              id,
              field_id,
              datapoint_id
            )
          )
        `,
        )
        .eq("field_id", fieldId);

      if (error) {
        console.error("Failed to load field usage:", error);
        return { templates: [], ruleReferences: [] };
      }

      const templates = data
        .map((tf) => ({
          id: tf.template?.id,
          name: tf.template?.name,
        }))
        .filter((t) => t.id && t.name);

      const ruleReferences = data.flatMap(
        (tf) =>
          tf.rule_sets
            ?.filter((rs) => rs.template_field_rules?.some((r) => r.field_id === fieldId))
            .map((rs) => ({
              ruleSetId: rs.id,
              ruleSetName: rs.name,
              templateId: tf.template?.id,
              templateName: tf.template?.name,
            })) || [],
      );

      return {
        templates: templates,
        ruleReferences: ruleReferences,
      };
    },
  });

  const createMutation = useMutation({
    mutationFn: async (ruleText: string) => {
      const { data, error } = await supabase
        .from("field_rules")
        .insert({
          field_id: fieldId,
          rule_text: ruleText,
          enabled: true,
        })
        .select();

      if (error) {
        toast.error("Failed to create rule");
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      toast.success("Rule added successfully");
      setNewRuleText("");
      setIsAddingNew(false);
      queryClient.invalidateQueries({ queryKey: ["field-rules", fieldId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (rule: Tables<"field_rules">) => {
      const { error } = await supabase
        .from("field_rules")
        .update({
          rule_text: rule.rule_text,
          enabled: rule.enabled,
        })
        .eq("id", rule.id);

      if (error) {
        toast.error("Failed to update rule");
        throw error;
      }
    },
    onSuccess: () => {
      toast.success("Rule updated successfully");
      setEditingRule(null);
      queryClient.invalidateQueries({ queryKey: ["field-rules", fieldId] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase.from("field_rules").update({ enabled }).eq("id", id);

      if (error) {
        toast.error("Failed to toggle rule");
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["field-rules", fieldId] });
      toast.success("Rule status updated");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("field_rules").delete().eq("id", id);

      if (error) {
        toast.error("Failed to delete rule");
        throw error;
      }
    },
    onSuccess: () => {
      toast.success("Rule deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["field-rules", fieldId] });
    },
  });

  const handleAddRule = () => {
    if (newRuleText.trim()) {
      createMutation.mutate(newRuleText);
    }
  };

  const handleUpdateRule = () => {
    if (editingRule?.rule_text.trim()) {
      updateMutation.mutate(editingRule);
    }
  };

  const handleToggleRule = (id: string, currentState: boolean) => {
    toggleMutation.mutate({
      id,
      enabled: !currentState,
    });
  };

  const handleDeleteRule = (id: string) => {
    if (confirm("Are you sure you want to delete this rule?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleCopyComplete = () => {
    // Nothing specific to do here, just to refresh the UI if needed
  };

  if (isLoading) {
    return <div>Loading rules...</div>;
  }

  return (
    <Tabs defaultValue="rules" className="space-y-4">
      <TabsList>
        <TabsTrigger value="rules">Field Rules</TabsTrigger>
        <TabsTrigger value="usage">Usage & References</TabsTrigger>
      </TabsList>

      <TabsContent value="rules">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex justify-between items-center">
              <span>Field Rules</span>
              <div className="flex items-center gap-2">
                {rules && rules.length > 0 && (
                  <Button onClick={() => setIsCopyDialogOpen(true)} size="sm" variant="outline">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Rules
                  </Button>
                )}
                <Button onClick={() => setIsAddingNew(true)} size="sm" disabled={isAddingNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rule
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isAddingNew && (
              <div className="border p-4 rounded-md bg-muted/50 space-y-2">
                <Textarea
                  value={newRuleText}
                  onChange={(e) => setNewRuleText(e.target.value)}
                  placeholder="Enter rule text..."
                  className="min-h-[100px]"
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setNewRuleText("");
                      setIsAddingNew(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleAddRule} disabled={!newRuleText.trim()}>
                    Save
                  </Button>
                </div>
              </div>
            )}

            {rules && rules.length === 0 && !isAddingNew && (
              <div className="text-center p-4 text-muted-foreground">
                No rules have been added yet. Click "Add Rule" to create one.
              </div>
            )}

            {rules?.map((rule) => (
              <div key={rule.id} className="border p-4 rounded-md space-y-2">
                {editingRule?.id === rule.id ? (
                  <>
                    <Textarea
                      value={editingRule.rule_text}
                      onChange={(e) => setEditingRule({ ...editingRule, rule_text: e.target.value })}
                      className="min-h-[100px]"
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setEditingRule(null)}>
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateRule} disabled={!editingRule.rule_text.trim()}>
                        Save
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-start">
                      <div className="flex-1 whitespace-pre-wrap">{rule.rule_text}</div>
                      <div className="flex items-center gap-2 ml-4">
                        <Switch
                          checked={rule.enabled}
                          onCheckedChange={() => handleToggleRule(rule.id, rule.enabled)}
                        />
                        <Button variant="ghost" size="icon" onClick={() => setEditingRule(rule)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteRule(rule.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">Status: {rule.enabled ? "Enabled" : "Disabled"}</div>
                  </>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="usage">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Field Usage & References</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-lg font-medium mb-2">Templates Using This Field</h3>
              {fieldUsage?.templates && fieldUsage.templates.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {fieldUsage.templates.map((template) => (
                    <Badge key={template.id} variant="outline">
                      {template.name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">This field is not used in any templates.</p>
              )}
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-medium mb-2">Referenced in Rule Sets</h3>
              {fieldUsage?.ruleReferences && fieldUsage.ruleReferences.length > 0 ? (
                <div className="space-y-2">
                  {fieldUsage.ruleReferences.map((ref, index) => (
                    <div key={index} className="p-2 border rounded">
                      <p className="font-medium">{ref.templateName}</p>
                      <p className="text-sm text-muted-foreground">Rule Set: {ref.ruleSetName}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">This field is not referenced in any rule sets.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Copy Rules Dialog */}
      <CopyRulesDialog
        open={isCopyDialogOpen}
        onOpenChange={setIsCopyDialogOpen}
        sourceFieldId={fieldId}
        sourceFieldName={fieldInfo?.name || "This field"}
        onCopyComplete={handleCopyComplete}
      />
    </Tabs>
  );
};
